import type {
  ApiErrorCode,
  IdentifyRequest,
  IdentifyResponse,
  MyVotesRequest,
  MyVotesResponse,
  PollResolveRequest,
  PollResolveResponse,
  VoteRequest,
  VoteResponse,
} from "@vote/shared";
import type { ApiFailure, ApiResult, VoteApi } from "../core/ports";

const DEFAULT_TIMEOUT_MILLISECONDS = 15_000;

/** HttpVoteApi の通信先・fetch実装・タイムアウトを組み立て時に差し替える設定。 */
interface HttpVoteApiOptions {
  endpoint: string;
  fetcher?: typeof fetch;
  timeoutMilliseconds?: number;
}

/** JSON値がプロパティ参照可能なオブジェクトかを確認する。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Retry-After の正の秒数表現だけを採用し、空・0・HTTP-dateは秒数不明として扱う。 */
function parseRetryAfter(value: string | null): number | undefined {
  if (value === null) {
    return undefined;
  }
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

/** サーバのエラー封筒をcore用の失敗結果へ変換し、壊れた封筒は内部エラーに寄せる。 */
function toFailure(payload: unknown, status: number, retryAfter: string | null): ApiFailure {
  const retryAfterSeconds = parseRetryAfter(retryAfter);
  if (isRecord(payload) && isRecord(payload.error)) {
    const { code, message } = payload.error;
    if (typeof code === "string" && typeof message === "string") {
      return {
        ok: false,
        error: {
          // SDKは実行時スキーマをバンドルしないため、サーバがsharedで検証したコードを信頼する。
          code: code as ApiErrorCode,
          message,
          ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
        },
      };
    }
  }

  return {
    ok: false,
    error: {
      code: "internal_error",
      message: `HTTP ${status} の応答形式が不正です`,
      ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
    },
  };
}

/**
 * APIの全エンドポイントをPOST JSON・Cookie同送で呼び出すブラウザアダプタ。
 * HTTP固有のResponseやステータスはcoreへ渡さず、型付き結果に変換する。
 */
export class HttpVoteApi implements VoteApi {
  private readonly endpoint: string;
  private readonly fetcher: typeof fetch;
  private readonly timeoutMilliseconds: number;

  public constructor(options: HttpVoteApiOptions) {
    this.endpoint = options.endpoint.replace(/\/+$/, "");
    this.fetcher = options.fetcher ?? ((input, init) => fetch(input, init));
    this.timeoutMilliseconds = options.timeoutMilliseconds ?? DEFAULT_TIMEOUT_MILLISECONDS;
  }

  /** visitorIdを任意で添えて、訪問者の発行・継続結果を返す。 */
  public identify(request: IdentifyRequest): Promise<ApiResult<IdentifyResponse>> {
    return this.post("/api/v1/identify", request);
  }

  /** 記事情報とvisitorIdを送り、表示対象の投票と回答状態を返す。 */
  public resolvePoll(request: PollResolveRequest): Promise<ApiResult<PollResolveResponse>> {
    return this.post("/api/v1/polls/resolve", request);
  }

  /** 選択した回答を保存し、保存直後の集計結果を返す。 */
  public vote(request: VoteRequest): Promise<ApiResult<VoteResponse>> {
    return this.post("/api/v1/votes", request);
  }

  /** 訪問者IDと任意cursorを送り、個人画面用の回答履歴ページを返す。 */
  public getMyVotes(request: MyVotesRequest): Promise<ApiResult<MyVotesResponse>> {
    return this.post("/api/v1/me/votes", request);
  }

  /** 共通のPOST条件・タイムアウト・エラー封筒変換を全APIへ適用する。 */
  private async post<T>(path: string, body: unknown): Promise<ApiResult<T>> {
    // AbortSignal.timeoutは手元ブラウザで未確認のため、明示的なControllerとtimerで中断する。
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMilliseconds);

    try {
      const response = await this.fetcher(`${this.endpoint}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        // nginx等がHTMLを返しても、HTTP状態とRetry-Afterを失わず失敗結果へ変換する。
        let errorPayload: unknown;
        try {
          errorPayload = await response.json();
        } catch {
          errorPayload = undefined;
        }
        return toFailure(errorPayload, response.status, response.headers.get("Retry-After"));
      }

      const payload: unknown = await response.json();
      // 成功JSONの実行時検証はsharedを使うサーバの責務とし、SDKを小さく保つ。
      return { ok: true, data: payload as T };
    } catch {
      return {
        ok: false,
        error: { code: "internal_error", message: "APIに接続できませんでした" },
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
